import { useState } from "react";
import { useAccount } from "wagmi";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Calculator,
  Zap,
} from "lucide-react";
import { cn, explorerUrl } from "../../lib/utils";
import { Link } from "react-router-dom";

export interface Farm {
  id: number;
  pair: string;
  token0Symbol: string;
  token1Symbol: string;
  apr: number;
  multiplier: string;
  liquidity: string;
  earned: string;
  staked: string;
  depositFee: number;
  contractAddress: string;
  isActive: boolean;
}

/* ------------------------------------------------------------------ */
/* Token pair icon colors - deterministic by symbol                     */
/* ------------------------------------------------------------------ */
const TOKEN_COLORS: Record<string, { bg: string; text: string }> = {
  JASMY: { bg: "bg-gradient-to-br from-[#F7931A] to-[#FFa832]", text: "text-[#070816]" },
  FUD: { bg: "bg-gradient-to-br from-[#FF6EC7] to-[#ff3ca8]", text: "text-[#070816]" },
  DJNN: { bg: "bg-gradient-to-br from-[#3b82f6] to-[#2563eb]", text: "text-white" },
  GENIE: { bg: "bg-gradient-to-br from-[#8B2FC9] to-[#6b21a8]", text: "text-white" },
  USDT: { bg: "bg-gradient-to-br from-[#26A17B] to-[#1a8a68]", text: "text-white" },
  WBTC: { bg: "bg-gradient-to-br from-[#F7931A] to-[#e67e00]", text: "text-white" },
};

function getTokenColor(symbol: string) {
  return TOKEN_COLORS[symbol] || { bg: "bg-gradient-to-br from-[#6366f1] to-[#4f46e5]", text: "text-white" };
}

/* ------------------------------------------------------------------ */
/* Multiplier badge color mapping                                      */
/* ------------------------------------------------------------------ */
function getMultiplierStyle(multiplier: string): {
  border: string;
  bg: string;
  text: string;
  shadow: string;
} {
  const num = parseInt(multiplier);
  if (num >= 40) {
    return {
      border: "border-[#F7931A]/60",
      bg: "bg-[#F7931A]/15",
      text: "text-[#F7931A]",
      shadow: "shadow-[0_0_12px_rgba(247,147,26,0.3)]",
    };
  }
  if (num >= 20) {
    return {
      border: "border-[#8B2FC9]/60",
      bg: "bg-[#8B2FC9]/15",
      text: "text-[#8B2FC9]",
      shadow: "shadow-[0_0_12px_rgba(139,47,201,0.3)]",
    };
  }
  if (num >= 10) {
    return {
      border: "border-[#39FF14]/50",
      bg: "bg-[#39FF14]/10",
      text: "text-[#39FF14]",
      shadow: "shadow-[0_0_12px_rgba(57,255,20,0.25)]",
    };
  }
  return {
    border: "border-white/20",
    bg: "bg-white/5",
    text: "text-white/50",
    shadow: "",
  };
}

/* ------------------------------------------------------------------ */
/* Card border gradient based on multiplier                            */
/* ------------------------------------------------------------------ */
function getCardGradient(multiplier: string): string {
  const num = parseInt(multiplier);
  if (num >= 40)
    return "linear-gradient(135deg, #F7931A, #FF6EC7, #F7931A)";
  if (num >= 20)
    return "linear-gradient(135deg, #8B2FC9, #FF6EC7, #8B2FC9)";
  if (num >= 10)
    return "linear-gradient(135deg, #39FF14, #00D4FF, #39FF14)";
  return "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05), rgba(255,255,255,0.2))";
}

function getCardGlow(multiplier: string, hasStake: boolean): string {
  if (!hasStake) return "";
  const num = parseInt(multiplier);
  if (num >= 40)
    return "0 0 20px rgba(247,147,26,0.15), 0 0 60px rgba(247,147,26,0.05)";
  if (num >= 20)
    return "0 0 20px rgba(139,47,201,0.15), 0 0 60px rgba(139,47,201,0.05)";
  if (num >= 10)
    return "0 0 20px rgba(57,255,20,0.12), 0 0 60px rgba(57,255,20,0.04)";
  return "";
}

/* ------------------------------------------------------------------ */
/* Token Icon component                                                */
/* ------------------------------------------------------------------ */
function TokenIcon({
  symbol,
  className,
}: {
  symbol: string;
  className?: string;
}) {
  const color = getTokenColor(symbol);
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full font-display font-bold text-xs select-none ring-2 ring-[#070816]",
        color.bg,
        color.text,
        className
      )}
    >
      {symbol.slice(0, 1)}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* FarmCard                                                            */
/* ------------------------------------------------------------------ */
interface FarmCardProps {
  farm: Farm;
  index: number;
}

export function FarmCard({ farm, index }: FarmCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { isConnected } = useAccount();

  const hasStake = parseFloat(farm.staked) > 0;
  const hasEarned = parseFloat(farm.earned) > 0;
  const multiplierStyle = getMultiplierStyle(farm.multiplier);
  const cardGradient = getCardGradient(farm.multiplier);
  const cardGlow = getCardGlow(farm.multiplier, hasStake);

  return (
    <div
      className="opacity-0 animate-fade-in"
      style={{ animationDelay: `${index * 80}ms`, animationFillMode: "forwards" }}
    >
      <div
        className="relative group rounded-[18px] p-[2px] transition-all duration-300 hover:-translate-y-[2px]"
        style={{
          background: cardGradient,
          boxShadow: cardGlow || undefined,
        }}
      >
        {/* Active indicator dot */}
        {hasStake && (
          <div className="absolute top-4 right-4 z-10">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#39FF14] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#39FF14]" />
            </span>
          </div>
        )}

        <div className="rounded-[16px] bg-[#070816] overflow-hidden">
          {/* ---- Header: Token pair + multiplier ---- */}
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                {/* Overlapping token icons */}
                <div className="relative flex-shrink-0" style={{ width: 52, height: 36 }}>
                  <TokenIcon
                    symbol={farm.token0Symbol}
                    className="absolute left-0 top-0 w-9 h-9 z-[2]"
                  />
                  <TokenIcon
                    symbol={farm.token1Symbol}
                    className="absolute left-[16px] top-0 w-9 h-9 z-[1]"
                  />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-text-primary leading-tight">
                    {farm.pair}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {farm.depositFee === 0 && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#39FF14]/80 bg-[#39FF14]/10 px-1.5 py-0.5 rounded">
                        No Fee
                      </span>
                    )}
                    {farm.isActive && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#00D4FF]/80 bg-[#00D4FF]/10 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <Zap className="w-2.5 h-2.5" /> Core
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Multiplier badge */}
              <div
                className={cn(
                  "px-3 py-1.5 rounded-full border font-display font-bold text-sm",
                  multiplierStyle.border,
                  multiplierStyle.bg,
                  multiplierStyle.text,
                  multiplierStyle.shadow
                )}
              >
                {farm.multiplier}
              </div>
            </div>

            {/* ---- APR Display ---- */}
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-text-tertiary font-medium">APR</span>
              <div className="flex items-center gap-2">
                <span className="neon-text-orange font-display text-2xl font-bold tracking-tight">
                  {farm.apr.toFixed(1)}%
                </span>
                <button
                  className="text-text-tertiary hover:text-primary transition-colors p-1 rounded-lg hover:bg-white/5"
                  title="APR Calculator"
                >
                  <Calculator className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="neon-divider my-4" />

            {/* ---- Earned Section ---- */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-tertiary uppercase tracking-wider font-semibold">
                  JASMY Earned
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={cn(
                      "font-display text-xl font-bold",
                      hasEarned ? "neon-text-orange" : "text-text-tertiary"
                    )}
                  >
                    {farm.earned}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    ~$0.00
                  </p>
                </div>
                <button
                  disabled={!hasEarned || !isConnected}
                  className={cn(
                    "btn-primary text-sm px-5 py-2",
                    !hasEarned && "opacity-30 cursor-not-allowed"
                  )}
                >
                  Harvest
                </button>
              </div>
            </div>

            <div className="neon-divider my-4" />

            {/* ---- Staked / Enable Section ---- */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-tertiary uppercase tracking-wider font-semibold">
                  {farm.pair} LP Staked
                </span>
              </div>

              {!isConnected ? (
                <button className="btn-neon w-full text-sm py-2.5 mt-1">
                  Connect Wallet
                </button>
              ) : hasStake ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-lg font-bold text-text-primary">
                      {farm.staked}
                    </p>
                    <p className="text-xs text-text-tertiary mt-0.5">
                      ~$0.00
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="btn-neon text-sm px-4 py-2">
                      -
                    </button>
                    <button className="btn-primary text-sm px-4 py-2">
                      +
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn-neon w-full text-sm py-2.5 mt-1 group/stake">
                  <span className="flex items-center justify-center gap-2">
                    Stake LP
                    <span className="opacity-0 group-hover/stake:opacity-100 transition-opacity">
                      &rarr;
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* ---- Details Toggle ---- */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-1.5 py-3 text-xs font-semibold text-text-tertiary hover:text-text-secondary transition-colors border-t border-white/[0.04] hover:bg-white/[0.02]"
          >
            Details
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>

          {/* ---- Expandable Details ---- */}
          <div
            className={cn(
              "overflow-hidden transition-all duration-300 ease-in-out",
              expanded ? "max-h-60 opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <div className="px-5 pb-5 pt-1 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-tertiary">Total Liquidity</span>
                <span className="text-sm font-semibold text-text-primary">
                  {farm.liquidity}
                </span>
              </div>

              {farm.depositFee > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-tertiary">Deposit Fee</span>
                  <span className="text-sm font-semibold text-warning">
                    {farm.depositFee}%
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-xs text-text-tertiary">LP Token</span>
                <Link
                  to="/liquidity"
                  className="text-xs font-medium text-primary hover:text-primary-hover transition-colors flex items-center gap-1"
                >
                  Get {farm.pair} LP <ExternalLink className="w-3 h-3" />
                </Link>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-text-tertiary">Contract</span>
                <a
                  href={explorerUrl("address", farm.contractAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-primary hover:text-primary-hover transition-colors flex items-center gap-1"
                >
                  View on Explorer <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-text-tertiary">Reward Token</span>
                <span className="text-sm font-semibold text-text-primary">JASMY</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
